import { useAppStateContext } from "@src/appV2/App/useAppState";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { useSkillsAssessments } from "@src/appV2/SkillsAssessment/api/useSkillsAssessments";
import { SkillsAssessmentWorkerStatus } from "@src/appV2/SkillsAssessment/types";

import { useHasSeenLastestOrientationFailureAlert } from "./useHasSeenLastestOrientationFailureAlert";

type OrientationAlertType = "ready-to-start" | "failed";

interface UseGetOrientationAlertDataProps {
  workerId: string;
}

interface OrientationAlertData {
  orientationAlertType: OrientationAlertType | undefined;
  scheduledFor: string | undefined;
  retakeAllowedAfter: string | undefined;
  scheduledEventId: string | undefined;
  isWorkerOnTime: boolean | undefined;
}

export function useGetOrientationAlertData(
  props: UseGetOrientationAlertDataProps
): OrientationAlertData {
  const { workerId } = props;
  let orientationAlertType: OrientationAlertType | undefined;
  const { isClipboardOrientationEnabled } = useSkillsAssessmentFeatureFlags();

  const {
    state: { isAppActive },
  } = useAppStateContext();

  const { data: assessments } = useSkillsAssessments(
    {
      workerId,
    },
    {
      enabled: isAppActive && isClipboardOrientationEnabled,
    }
  );

  const assessmentStatus = assessments?.included.find(
    (included) => included.type === "assessment-status"
  );
  const assessmentEvent = assessments?.included.find(
    (included) => included.type === "assessment-event"
  );
  const {
    status: assessmentStatusValue,
    isAllowedToStartAssessment,
    retakeAllowedAfter,
  } = assessmentStatus?.attributes ?? {};
  const { id: assessmentEventId, attributes: assessmentEventAttributes } = assessmentEvent ?? {};
  const { scheduledFor } = assessmentEventAttributes ?? {};
  const workerHasFailedOrientation = assessmentStatusValue === SkillsAssessmentWorkerStatus.FAILED;
  const {
    isLoading: isLoadingHasSeenLastestOrientationFailureAlert,
    hasSeenLastestOrientationFailureAlert,
  } = useHasSeenLastestOrientationFailureAlert(assessmentEventId);

  if (isAllowedToStartAssessment) {
    orientationAlertType = "ready-to-start";
  } else if (
    workerHasFailedOrientation &&
    !isLoadingHasSeenLastestOrientationFailureAlert &&
    !hasSeenLastestOrientationFailureAlert
  ) {
    orientationAlertType = "failed";
  }

  return {
    isWorkerOnTime: assessmentStatus?.attributes?.isStartedOnTime,
    orientationAlertType,
    scheduledFor,
    retakeAllowedAfter,
    scheduledEventId: assessmentEventId,
  };
}
